/** Manages an enemy's skill loadout: registration, activation, interruption, and per-frame updates. */
import type { EnemySkillEffect } from './EnemySkillEffect';
import type { EnemyStamina } from './EnemyStamina';
import { EnemyBlockSkillEffect } from './EnemyBlockSkillEffect';
import { EnemyBuffSkillEffect } from './EnemyBuffSkillEffect';
import { EnemyBackupSkillEffect } from './EnemyBackupSkillEffect';

/** Context passed to skill selection for tactical preconditions. */
export interface SkillSelectionContext {
  distToPlayer: number;
  hasLOS: boolean;
  recentlyDamaged: boolean;
  alliesInRange: number;
}

/** Manages an enemy's skill loadout, enforcing single-skill-at-a-time and ticking cooldowns. */
export class EnemySkillRegistry {
  private skills: Map<string, EnemySkillEffect> = new Map();
  private _skillOrder: string[] = [];
  private _activeSkill: EnemySkillEffect | null = null;
  private _stamina: EnemyStamina | null = null;

  /** Register a skill effect by its ID. Order of registration = priority order. */
  register(skill: EnemySkillEffect): void {
    this.skills.set(skill.skillId, skill);
    this._skillOrder.push(skill.skillId);
  }

  /** Set the stamina pool (optional — only needed if archetype has dash). */
  setStamina(stamina: EnemyStamina): void {
    this._stamina = stamina;
  }

  /** The stamina pool, if any. */
  get stamina(): EnemyStamina | null { return this._stamina; }

  /** The currently executing skill, or null if none active. */
  get activeSkill(): EnemySkillEffect | null {
    return this._activeSkill;
  }

  /** Look up a registered skill by ID. */
  getSkill(id: string): EnemySkillEffect | undefined {
    return this.skills.get(id);
  }

  /** All registered skill IDs in priority order. */
  get skillIds(): readonly string[] {
    return this._skillOrder;
  }

  /** Iterate all registered skills in priority order. */
  allSkills(): IterableIterator<[string, EnemySkillEffect]> {
    const order = this._skillOrder;
    const skills = this.skills;
    return (function* () {
      for (const id of order) {
        yield [id, skills.get(id)!] as [string, EnemySkillEffect];
      }
    })();
  }

  /**
   * Select the highest-priority available skill given the current context.
   * Returns the skill ID, or null if nothing is available.
   * Skills are checked in registration order (= JSON array order = priority order).
   */
  getAvailableSkill(context: SkillSelectionContext): string | null {
    if (this._activeSkill?.isActive?.()) return null;

    for (const id of this._skillOrder) {
      const skill = this.skills.get(id)!;

      // Must be activatable (not on cooldown, not already active)
      if (skill.canActivate && !skill.canActivate()) continue;

      // Range check: if skill has a defined range, player must be within it
      if (skill.skillRange !== undefined && context.distToPlayer > skill.skillRange) continue;

      // LOS required for all offensive skills
      if (!context.hasLOS) continue;

      // Tactical preconditions
      if (skill instanceof EnemyBlockSkillEffect && !context.recentlyDamaged) continue;
      if (skill instanceof EnemyBuffSkillEffect && context.alliesInRange <= 0) continue;
      if (skill instanceof EnemyBackupSkillEffect && !context.recentlyDamaged) continue;

      return id;
    }

    return null;
  }

  /**
   * Attempt to activate a skill by ID.
   * Returns true if the skill started successfully.
   * Fails if another skill is already active or this skill is on cooldown.
   */
  requestSkill(id: string): boolean {
    if (this._activeSkill?.isActive?.()) return false;

    const skill = this.skills.get(id);
    if (!skill) return false;
    if (skill.canActivate && !skill.canActivate()) return false;

    const started = skill.activate ? skill.activate() : false;
    if (started) {
      this._activeSkill = skill;
    }
    return started;
  }

  /** Interrupt the active skill (e.g. on stagger). */
  interrupt(): void {
    if (this._activeSkill) {
      this._activeSkill.interrupt();
      this._activeSkill = null;
    }
  }

  /** Tick all registered skills (active skill for phase timing, others for cooldown). Also ticks stamina. */
  update(delta: number): void {
    // Tick stamina regen
    if (this._stamina) {
      this._stamina.update(delta);
    }

    for (const skill of this.skills.values()) {
      skill.update(delta);
    }

    // Clear active reference once the skill completes
    if (this._activeSkill && this._activeSkill.isComplete()) {
      this._activeSkill = null;
    }
  }

  destroy(): void {
    for (const skill of this.skills.values()) {
      skill.destroy();
    }
    this.skills.clear();
    this._activeSkill = null;
  }
}
